/**
 * THE SOURCE-PRECEDENCE CONTROL — PLACEMENT ONLY, NOT WIRED (2026-09-23, owner direction; PENDING
 * APPROVAL).
 *
 * The owner's spec: "a segmented control, NOT a checkbox, attached to the upload card, one row each
 * for subject / comparable sales / listings, left option selected by default. Per-field include
 * checkboxes stay in the field list. Build it after S09, and show me the placement before wiring it."
 *
 * So this draws the control where it will live and does nothing else: the choice is held in local
 * state and read by no request. THAT WOULD BE A DEAD CONTROL IF A CUSTOMER COULD SEE IT, so it
 * renders only in a dev build or behind `?preview=precedence`, and it says in its own box that it is
 * a preview and is not applied. The option wording ("Public record first" / "My file first") is a
 * proposal for the owner, matching the rule already shipped tonight: the file is read only when the
 * public record left something to read it for, or when the agent chooses their own data.
 */
import { useState } from "react";

import { themeVar as colors } from "../design-tokens";

export type Precedence = "record" | "file";

const ROWS: { key: "subject" | "comps" | "listings"; label: string }[] = [
  { key: "subject", label: "Subject" },
  { key: "comps", label: "Comparable sales" },
  { key: "listings", label: "Listings" },
];

const OPTIONS: { value: Precedence; label: string }[] = [
  { value: "record", label: "Public record first" },
  { value: "file", label: "My file first" },
];

/** Shown only where an unwired control cannot mislead a customer. */
export function precedencePreviewEnabled(search: string, devBuild: boolean): boolean {
  return devBuild || new URLSearchParams(search).get("preview") === "precedence";
}

export function SourcePrecedencePreview() {
  const [choice, setChoice] = useState<Record<string, Precedence>>(
    { subject: "record", comps: "record", listings: "record" });
  return (
    <section data-testid="source-precedence" aria-labelledby="vb-precedence-heading"
             style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8,
                      border: `1px dashed ${colors.border}`, background: colors.bgSurface }}>
      <h2 id="vb-precedence-heading" style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>
        When your file and the public record disagree
      </h2>
      <p style={{ margin: "3px 0 8px", fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5 }}>
        Preview — placement only. Choices here are not applied yet.
      </p>
      <div style={{ display: "grid", gap: 6 }}>
        {ROWS.map((row) => (
          <div key={row.key} role="radiogroup" aria-label={row.label}
               style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                        flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: colors.textPrimary }}>{row.label}</span>
            <span style={{ display: "inline-flex", border: `1px solid ${colors.border}`, borderRadius: 7,
                           overflow: "hidden" }}>
              {OPTIONS.map((opt, i) => {
                const on = choice[row.key] === opt.value;
                return (
                  <button key={opt.value} type="button" role="radio" aria-checked={on}
                          data-precedence={`${row.key}:${opt.value}`}
                          onClick={() => setChoice((c) => ({ ...c, [row.key]: opt.value }))}
                          style={{ padding: "4px 10px", fontSize: 11.5, border: "none",
                                   borderLeft: i ? `1px solid ${colors.border}` : "none",
                                   background: on ? colors.accent : "transparent",
                                   color: on ? "#fff" : colors.textPrimary, cursor: "pointer",
                                   fontWeight: on ? 600 : 500 }}>
                    {opt.label}
                  </button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
